import { QuickstartActionPage } from './QuickstartActionPage';
import { QuickstartCard } from './QuickstartCard';
import { ThemeLogo } from './ThemeLogo';
import {
  QuickstartTheme,
  getWishlistButtonImage,
  getResponsiveTextSize,
  getResponsiveSpacing,
  getResolvedCtaUrl,
} from '../types/theme';
import { getOutboundCtaUrl } from '../utils/steamUrls';
import { useIsLandscape, useForcePortrait } from '../utils/responsiveHooks';

interface QuickstartCTAProps {
  theme: QuickstartTheme;
  urlSlug: string;
  hasSlugConfig: boolean;
  exitReason?: 'afk_timeout' | 'ad_timer_expired' | 'wishlist_clicked' | 'manual_exit' | 'invitation_invalidated' | 'loading_timeout' | 'matchmaking_failed' | 'broadcast_join_failed' | 'auth_failed' | 'device_not_supported' | 'match_ended';
  onWishlistCTAClicked: () => void;
}

export const QuickstartCTA = ({ theme, urlSlug, hasSlugConfig, exitReason, onWishlistCTAClicked }: QuickstartCTAProps) => {
  const getMessage = () => {
    switch (exitReason) {
      case 'afk_timeout':
        return 'Your session timed out due to inactivity';
      case 'ad_timer_expired':
        return 'Thanks for playing the demo!';
      case 'wishlist_clicked':
        return 'Thanks for your interest!';
      case 'invitation_invalidated':
        return 'Thanks for playing the demo!';
      case 'match_ended':
        return 'The match has ended — thanks for playing!';
      default:
        return 'Thanks for playing!';
    }
  };

  const handleButtonClick = () => {
    onWishlistCTAClicked();

    // Open in new tab instead of redirecting (prevents canceling analytics request)
    const ctaUrl = getResolvedCtaUrl(theme);
    if (ctaUrl) {
      const finalUrl = getOutboundCtaUrl(ctaUrl, hasSlugConfig);
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Check if using custom CTA layout
  const hasCustomLayout = !!(
    theme.ctaHeaderText ||
    theme.ctaSubheaderText ||
    theme.ctaMessageText
  );

  const isLandscape = useIsLandscape();
  const { shouldRotate } = useForcePortrait();

  if (hasCustomLayout) {
    return (
      <div
        className='flex items-center justify-center overflow-auto'
        style={
          shouldRotate ? {
            // Landscape mode: force portrait by rotating -90deg
            position: 'fixed',
            left: '50%',
            top: '50%',
            width: '100dvh',
            height: '100dvw',
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
            backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '1rem',
          } : {
            // Portrait mode: normal layout
            minHeight: '100vh',
            backgroundImage: theme.pageBackgroundImage ? `url(${theme.pageBackgroundImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: isLandscape ? '0.25rem 1rem' : '1rem',
          }
        }
      >
        <QuickstartCard theme={theme} showHeader={false} hideFooter={true}>
          <div className='flex flex-col items-center text-center'>
            {/* Studio Logo */}
            {!theme.hideStudioLogoOnCTA && (
              <ThemeLogo
                kind='studio'
                theme={theme}
                isLandscape={isLandscape}
                className={getResponsiveSpacing('mt', 4, isLandscape)}
              />
            )}

            {/* Custom Header Text */}
            {theme.ctaHeaderText && (
              <div className={getResponsiveSpacing('mt', 8, isLandscape)}>
                <div
                  className={`font-["Inter"] ${getResponsiveTextSize('3xl', isLandscape)} font-bold`}
                  style={{ color: theme.primaryColor }}
                >
                  {theme.ctaHeaderText}
                </div>
              </div>
            )}

            {/* Game Logo */}
            {!theme.hideGameLogoOnCTA && (
              <ThemeLogo
                kind='game'
                theme={theme}
                isLandscape={isLandscape}
                className={theme.ctaHeaderText ? getResponsiveSpacing('mt', 6, isLandscape) : getResponsiveSpacing('mt', 12, isLandscape)}
                loading='eager'
              />
            )}

            {/* ConnectWithUsSection removed - use requireAuth instead for simpler auth flow */}

            {/* Wishlist/Store Button */}
            {getResolvedCtaUrl(theme) && (() => {
              const buttonImage = getWishlistButtonImage(theme);
              return (
                <div className={`${getResponsiveSpacing('mt', 8, isLandscape)} w-full`}>
                  <img
                    src={buttonImage.src}
                    alt={buttonImage.alt}
                    onClick={handleButtonClick}
                    className='mx-auto cursor-pointer hover:opacity-90'
                    style={{ height: isLandscape ? '48px' : '64px' }}
                  />
                </div>
              );
            })()}

            {/* Powered by Playcast */}
            <ThemeLogo
              kind='poweredBy'
              theme={theme}
              isLandscape={isLandscape}
              className={`${getResponsiveSpacing('mb', 4, isLandscape)} ${getResponsiveSpacing('mt', 12, isLandscape)}`}
            />
          </div>
        </QuickstartCard>
      </div>
    );
  }

  // Default: wishlist_only variant (use existing QuickstartActionPage)
  console.log('[QuickstartCTA] Message:', getMessage());
  return <QuickstartActionPage theme={theme} onButtonClick={handleButtonClick} />;
};

